/**
 * plotRegion — PDF-4 plot-area resolution (DXF group code 74).
 *
 * Resolves which portion of the plotted space maps onto the printable sheet
 * and at what scale, returning the inputs the page transform needs
 * (`regionOrigin`, `scaleOverride`, `centeringOffsetMm`). `PLAN-plot-pdf.md` §2
 * defines the six plot-area types:
 *
 *   - **Layout (5)** — the full printable area (default for layouts). Region
 *     origin (0,0), scale 1, no centering. Byte-identical to PDF-1/2/3.
 *   - **Window (4)** — the saved window rectangle (codes 48/49/140/141), in the
 *     plotted space's coordinates. Scaled to fit the printable area (or by the
 *     explicit numerator/denominator when the UseStandardScale flag is set),
 *     centred inside it.
 *   - **Extents (1)** — the bounding box of all PLOTTABLE + VISIBLE entities in
 *     the plotted (paper) space, fit + centred like Window. Empty space →
 *     `empty: true` so the caller emits a blank page + warning (§7.6).
 *   - **Limits (2)** — the layout LIMMIN..LIMMAX rectangle (model.ts
 *     `minimumLimits`/`maximumLimits`, DXF 10/20 / 11/21) when available; else
 *     falls back to Extents with a note.
 *   - **View (3)** — a named saved view. The model does not expose a per-layout
 *     named-view list, so this falls back to Layout with a note (PDF-4b).
 *   - **Display (0)** — the current on-screen view; headless has no "current
 *     view" so this is treated as Layout with a note.
 *
 * # Two-scale separation (PLAN §2)
 * This module resolves the LAYOUT sheet → paper scale only (the first scale).
 * The per-VP model → paper scale is owned by `viewportPlot.ts` and is applied
 * INSIDE the composed VP transform; it is independent of the plot area.
 *
 * # Documented deferrals (CLAUDE.md PRIORITY #1 — note, do NOT fake)
 *   - **Extents incl. VP-visible model extents → PDF-4b.** AutoCAD's layout
 *     Extents includes each VP's border PLUS the model geometry visible through
 *     that VP, projected onto paper. PDF-4 computes Extents from the plotted
 *     space's OWN entities (incl. the VP rectangles, via the VIEWPORT entity's
 *     paper-space corners). The VP-model-content contribution is PDF-4b — see
 *     {@link ExtentsVpModelNote}. We do NOT silently approximate it.
 *   - **View (3) named-view region → PDF-4b.** No per-layout named-view list in
 *     the model (`doc.savedView` is a single model-level optional). Falls back
 *     to Layout — see {@link ViewAreaNote}.
 */

import {
  PlotAreaType,
  PlotLayoutFlags,
  printableAreaMm,
  type DrawingDocument,
  type DrawingSpace,
  type LayoutPlotSettings,
  type ViewportEntity,
} from "@dxf-ai-editor/drawing-model";

import {
  type CadConvertContext,
  type PathSegment,
  type Point2,
} from "./cadPrimitive";
import { entityToPrimitives } from "./entityToPrimitives";
import { resolvePlotScale } from "./pdfTransform";
import {
  isControlViewport,
  isPaperEntityVisible,
  isViewportOffOrDegenerate,
  type LayerVisibilityMap,
} from "./viewportPlot";

/**
 * PDF-4b deferral note: layout-Extents should also include each VP's
 * model-visible content projected to paper. PDF-4 computes Extents from the
 * plotted space's own paper-space entities (including VP rectangles); the
 * VP-model-content contribution is deferred. Documented, not faked.
 */
export const ExtentsVpModelNote =
  "PDF-4b: layout Extents excludes VP-visible model extents (own paper-space entities + VP rects only)";

/**
 * PDF-4b deferral note: View (3) plot area falls back to Layout because the
 * model exposes no per-layout named-view list (only a single model-level
 * `doc.savedView`). Documented, not faked.
 */
export const ViewAreaNote =
  "PDF-4b: View (3) plot area falls back to Layout (no per-layout named-view list in model)";

/** A 2D axis-aligned rectangle in plotted-space mm. */
export interface RegionRect {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** The resolved plot region the page transform consumes. */
export interface ResolvedPlotRegion {
  /**
   * Lower-left of the plotted region in plotted-space mm (subtracted first by
   * the transform — §4.1 near-origin localize). For Layout this is (0,0).
   */
  regionOrigin: { x: number; y: number };
  /** Effective sheet → paper scale factor (world-mm → paper-mm). */
  scale: number;
  /**
   * Extra centering offset in mm, added alongside `settings.offset`. Centres a
   * fit / centred region inside the printable area; (0,0) for Layout.
   */
  centeringOffsetMm: { x: number; y: number };
  /** The resolved region size (width × height) in plotted-space mm. */
  region: { width: number; height: number };
  /**
   * True when there is nothing to plot (empty Extents space, or printable area
   * collapsed because margins exceed the paper §7.3). The caller emits a blank
   * page + a warning rather than dividing by zero.
   */
  empty: boolean;
  /** Diagnostic / report string when a fallback or empty case is hit. */
  note?: string;
}

/** Context for {@link resolvePlotRegion} (Extents needs to walk geometry). */
export interface PlotRegionContext {
  /** Converter context (layer colours / blocks / tolerance) for Extents. */
  ctx: CadConvertContext;
  /** Layer plot-visibility map (Extents counts plottable + visible only). */
  layers: LayerVisibilityMap;
}

/** Whether the UseStandardScale flag (bit 16) is set. */
function usesStandardScale(settings: LayoutPlotSettings): boolean {
  return (settings.flags & PlotLayoutFlags.UseStandardScale) !== 0;
}

/**
 * Fit a region rectangle into the printable area: scale = min(printableW/regW,
 * printableH/regH), centring offset = half the printable slack. Returns the
 * scale and the centering offset (in printable-local mm).
 *
 * The centering offset positions the scaled region inside the printable area:
 * after the transform subtracts `regionOrigin`, the region's lower-left sits at
 * printable (0,0); the centering offset pushes it to the centred position.
 */
function fitRegion(
  region: { width: number; height: number },
  printable: { width: number; height: number },
  explicitScale: number | undefined,
): { scale: number; centering: { x: number; y: number } } {
  const scale =
    explicitScale !== undefined &&
    Number.isFinite(explicitScale) &&
    explicitScale > 0
      ? explicitScale
      : Math.min(
          printable.width / region.width,
          printable.height / region.height,
        );
  // Slack = printable − region×scale; centre = half the slack (clamped ≥ 0 so an
  // over-size explicit scale does not push content off the LL corner).
  const slackX = printable.width - region.width * scale;
  const slackY = printable.height - region.height * scale;
  return {
    scale,
    centering: { x: Math.max(slackX, 0) / 2, y: Math.max(slackY, 0) / 2 },
  };
}

/** A degenerate (zero / negative area) region cannot be fit. */
function isDegenerate(region: { width: number; height: number }): boolean {
  return !(region.width > 0) || !(region.height > 0);
}

/** Accumulate one subpath's vertices (start + each segment control/end). */
function accSubpath(
  acc: (x: number, y: number) => void,
  start: Point2,
  segments: PathSegment[],
): void {
  acc(start.x, start.y);
  for (const seg of segments) {
    if (seg.kind === "line") {
      acc(seg.to.x, seg.to.y);
    } else {
      acc(seg.c1.x, seg.c1.y);
      acc(seg.c2.x, seg.c2.y);
      acc(seg.to.x, seg.to.y);
    }
  }
}

/**
 * Bounding box of all PLOTTABLE + VISIBLE entities in `space` (paper space).
 * Walks each entity → `entityToPrimitives` → min/max over every path vertex
 * (start + each segment's control/end points) AND every text-run anchor. The
 * VIEWPORT entity's paper-space rectangle is included via its corners (the VP
 * border participates in layout Extents). Returns null when nothing plottable.
 */
function computeExtentsRect(
  space: DrawingSpace,
  context: PlotRegionContext,
): RegionRect | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let any = false;

  const acc = (x: number, y: number): void => {
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
    any = true;
  };

  for (const entity of space.entities) {
    if (!isPaperEntityVisible(entity.layer, context.layers)) continue;

    if (entity.type === "Viewport") {
      // VP rectangle (paper-space corners) participates in layout Extents —
      // but ONLY for viewports the renderer actually plots. FIX D (Codex
      // #180): skip the control VP (viewportId 1, sheet-sized) and off /
      // degenerate VPs, exactly the filter `renderSheetPage` uses. Including
      // the control VP made Extents zoom to the whole sheet instead of the
      // real content.
      const vp = entity as ViewportEntity;
      if (isControlViewport(vp) || isViewportOffOrDegenerate(vp)) continue;
      if (vp.width > 0 && vp.height > 0) {
        const hw = vp.width / 2;
        const hh = vp.height / 2;
        acc(vp.center.x - hw, vp.center.y - hh);
        acc(vp.center.x + hw, vp.center.y + hh);
      }
      continue;
    }

    for (const p of entityToPrimitives(entity, context.ctx)) {
      if (p.type === "path") {
        accSubpath(acc, p.start, p.segments);
        for (const sub of p.subpaths ?? []) {
          accSubpath(acc, sub.start, sub.segments);
        }
      } else if (p.type === "hatch-pattern" || p.type === "gradient-hatch") {
        // PDF-5a pattern hatch / PDF-6.5b gradient hatch: its boundary loops
        // define the plotted extent (the pattern lines / shading are clipped to
        // them, so the loops are the bound).
        for (const loop of p.loops) {
          accSubpath(acc, loop.start, loop.segments);
        }
      } else {
        // text-run: anchor only (full glyph extents are font-metric dependent;
        // the anchor is the AutoCAD insertion point, the dominant bound term).
        acc(p.position.x, p.position.y);
      }
    }
  }

  if (!any) return null;
  return { minX, minY, maxX, maxY };
}

/** Resolve a {@link RegionRect} to a fit + centred {@link ResolvedPlotRegion}. */
function regionFromRect(
  rect: RegionRect,
  settings: LayoutPlotSettings,
  printable: { width: number; height: number },
  note?: string,
): ResolvedPlotRegion {
  const region = {
    width: rect.maxX - rect.minX,
    height: rect.maxY - rect.minY,
  };
  if (isDegenerate(region)) {
    return {
      regionOrigin: { x: rect.minX, y: rect.minY },
      scale: 1,
      centeringOffsetMm: { x: 0, y: 0 },
      region,
      empty: true,
      note: note ?? "degenerate region (zero extent)",
    };
  }
  const explicit = usesStandardScale(settings)
    ? resolvePlotScale(settings)
    : undefined;
  const { scale, centering } = fitRegion(region, printable, explicit);
  return {
    regionOrigin: { x: rect.minX, y: rect.minY },
    scale,
    centeringOffsetMm: centering,
    region,
    empty: false,
    note,
  };
}

/** The trivial Layout region (origin 0, scale 1, no centering). */
function layoutRegion(
  printable: { width: number; height: number },
  note?: string,
): ResolvedPlotRegion {
  return {
    regionOrigin: { x: 0, y: 0 },
    scale: 1,
    centeringOffsetMm: { x: 0, y: 0 },
    region: { width: printable.width, height: printable.height },
    empty: false,
    note,
  };
}

/**
 * Resolve the plot region for a sheet. `space` is the plotted (paper) space;
 * `doc` provides the layout LIMMIN/LIMMAX (Limits area). `context` is only
 * needed for Extents (geometry walk) but is always required so the signature is
 * stable across area types.
 */
export function resolvePlotRegion(
  settings: LayoutPlotSettings,
  doc: DrawingDocument,
  space: DrawingSpace,
  context: PlotRegionContext,
): ResolvedPlotRegion {
  const printable = printableAreaMm(settings);

  // §7.3 guard: margins exceed paper → printable collapsed → refuse (no div/0).
  if (isDegenerate(printable)) {
    return {
      regionOrigin: { x: 0, y: 0 },
      scale: 1,
      centeringOffsetMm: { x: 0, y: 0 },
      region: { width: 0, height: 0 },
      empty: true,
      note: "printable area ≤ 0 (margins exceed paper, §7.3)",
    };
  }

  switch (settings.plotAreaType) {
    case PlotAreaType.Layout:
      return layoutRegion(printable);

    case PlotAreaType.Window: {
      const w = settings.window;
      const rect: RegionRect = {
        minX: Math.min(w.lowerLeftX, w.upperRightX),
        minY: Math.min(w.lowerLeftY, w.upperRightY),
        maxX: Math.max(w.lowerLeftX, w.upperRightX),
        maxY: Math.max(w.lowerLeftY, w.upperRightY),
      };
      return regionFromRect(rect, settings, printable);
    }

    case PlotAreaType.Extents: {
      const rect = computeExtentsRect(space, context);
      if (!rect) {
        // §7.6: empty space → blank page + warning (caller emits both).
        return {
          regionOrigin: { x: 0, y: 0 },
          scale: 1,
          centeringOffsetMm: { x: 0, y: 0 },
          region: { width: 0, height: 0 },
          empty: true,
          note: `empty space (no plottable geometry); ${ExtentsVpModelNote}`,
        };
      }
      return regionFromRect(rect, settings, printable, ExtentsVpModelNote);
    }

    case PlotAreaType.Limits: {
      // LIMMIN/LIMMAX live on the Layout record (model.ts minimumLimits /
      // maximumLimits, DXF 10/20 / 11/21). Fall back to Extents with a note
      // when the layout is absent or the rectangle is degenerate.
      const layout = doc.layoutsBySpaceId?.[space.id];
      if (layout) {
        const rect: RegionRect = {
          minX: Math.min(layout.minimumLimits.x, layout.maximumLimits.x),
          minY: Math.min(layout.minimumLimits.y, layout.maximumLimits.y),
          maxX: Math.max(layout.minimumLimits.x, layout.maximumLimits.x),
          maxY: Math.max(layout.minimumLimits.y, layout.maximumLimits.y),
        };
        if (!isDegenerate({ width: rect.maxX - rect.minX, height: rect.maxY - rect.minY })) {
          return regionFromRect(rect, settings, printable);
        }
      }
      // Fallback: Extents (documented).
      const rect = computeExtentsRect(space, context);
      const note =
        "Limits fell back to Extents (no LIMMIN/LIMMAX on layout or degenerate)";
      if (!rect) {
        return {
          regionOrigin: { x: 0, y: 0 },
          scale: 1,
          centeringOffsetMm: { x: 0, y: 0 },
          region: { width: 0, height: 0 },
          empty: true,
          note: `${note}; empty space`,
        };
      }
      return regionFromRect(rect, settings, printable, note);
    }

    case PlotAreaType.View:
      // No per-layout named-view list in the model → fall back to Layout
      // (PDF-4b). Documented, NOT faked.
      return layoutRegion(printable, ViewAreaNote);

    case PlotAreaType.Display:
    default:
      // Headless: no "current on-screen view" → treat as Layout with a note.
      return layoutRegion(
        printable,
        "Display (0) treated as Layout (headless has no current view)",
      );
  }
}
